/**
 * Front chat widget loader.
 *
 * Front is a customer-support live chat, not a tracker — it is treated as an
 * always-on support tool (no consent gate) but is route-scoped: the launcher
 * shows on owner-facing surfaces and is hidden on public share/collect pages
 * (see `FrontChatController`). Because of that route scoping and the "no consent
 * gate" decision, it deliberately lives outside the consent tracker registry.
 *
 * A blank `VITE_FRONT_CHAT_ID` disables the widget entirely (nothing is
 * injected), mirroring the Sentry-DSN / tracker-id "blank disables" convention.
 *
 * The module keeps a tiny state machine so the bundle is injected exactly once
 * and reused across navigations: `init` shows Front's default launcher, and
 * `shutdown` tears it down when the user lands on an excluded route. All calls
 * are idempotent and safe under React StrictMode's double-invoke.
 */

const BUNDLE_SRC = "https://chat-assets.frontapp.com/v1/chat.bundle.js";

const chatId: string = import.meta.env.VITE_FRONT_CHAT_ID ?? "";

/** Script lifecycle: not yet requested → loading → the bundle is ready. */
type ScriptState = "idle" | "loading" | "ready";

let scriptState: ScriptState = "idle";
/** Whether the current route wants the launcher visible. */
let desiredVisible = false;
/** Whether Front is currently initialized (launcher mounted). */
let initialized = false;

/** True only when a (non-blank) chat id is configured. */
export function isFrontChatConfigured(): boolean {
  return chatId.length > 0;
}

/** Bring Front's state in line with `desiredVisible`, once the bundle is ready. */
function reconcile(): void {
  if (scriptState !== "ready" || typeof window.FrontChat !== "function") return;
  if (desiredVisible && !initialized) {
    window.FrontChat("init", { chatId, useDefaultLauncher: true });
    initialized = true;
  } else if (!desiredVisible && initialized) {
    window.FrontChat("shutdown");
    initialized = false;
  }
}

/** Inject the Front bundle once; reconcile as soon as it finishes loading. */
function ensureScript(): void {
  if (scriptState !== "idle") return;
  scriptState = "loading";
  const script = document.createElement("script");
  script.async = true;
  script.src = BUNDLE_SRC;
  script.addEventListener("load", () => {
    scriptState = "ready";
    reconcile();
  });
  document.head.appendChild(script);
}

/**
 * Show or hide the Front launcher for the current route. No-op when the widget
 * is unconfigured. Showing lazily injects the bundle on first use; hiding tears
 * the launcher down without unloading the (cached) bundle.
 */
export function setFrontChatVisible(visible: boolean): void {
  if (!isFrontChatConfigured()) return;
  desiredVisible = visible;
  if (visible) ensureScript();
  reconcile();
}
